import React from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { reportClientError } from "../../utils/errorReporting";

export type ErrorBoundaryVariant = "page" | "section";

export interface ErrorBoundaryProps {
  /** "page": full-width card with reload / back buttons. "section": inline strip with Retry. */
  variant: ErrorBoundaryVariant;
  /** Section name shown in the inline strip. */
  label?: string;
  /** Any value; when it changes the boundary clears its error and re-renders its children. */
  resetKey?: unknown;
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  showDetails: boolean;
}

const STACK_LINES = 20;

const toError = (value: unknown): Error =>
  value instanceof Error
    ? value
    : new Error(typeof value === "string" ? value : String(value));

const detailsText = (error: Error): string => {
  const stack = (error.stack || "").split("\n").slice(0, STACK_LINES).join("\n");
  return stack ? `${error.message}\n\n${stack}` : error.message;
};

/**
 * Catches render errors below it so a crash never white-screens the app.
 * Report goes to the server via `reportClientError` (source "boundary").
 * Class component on purpose — React has no hook equivalent — and it must not
 * use router hooks: the root boundary sits outside the Router.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, showDetails: false };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: toError(error), showDetails: false };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    const err = toError(error);
    reportClientError({
      message: err.message || String(error),
      stack: err.stack,
      componentStack: info?.componentStack || undefined,
      source: "boundary",
      route: window.location.pathname + window.location.search,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && !Object.is(prevProps.resetKey, this.props.resetKey)) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ error: null, showDetails: false });
  };

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  renderSection() {
    const { label } = this.props;
    return (
      <Alert
        severity="error"
        data-error-boundary="section"
        action={
          <Button color="inherit" size="small" onClick={this.reset}>
            Retry
          </Button>
        }
        sx={{ alignItems: "center" }}
      >
        This section failed to load.
        {label ? (
          <Box component="span" sx={{ ml: 0.5, opacity: 0.8 }}>
            ({label})
          </Box>
        ) : null}
      </Alert>
    );
  }

  renderPage(error: Error) {
    const { showDetails } = this.state;
    return (
      <Box
        data-error-boundary="page"
        sx={{ display: "flex", justifyContent: "center", px: 2, py: { xs: 4, md: 8 } }}
      >
        <Paper
          variant="outlined"
          role="alert"
          sx={{ p: { xs: 2.5, md: 4 }, width: "100%", maxWidth: 640, borderRadius: "12px" }}
        >
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Something went wrong on this page
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            The error has been reported. You can reload or go back to the dashboard.
          </Typography>

          <Button
            size="small"
            onClick={this.toggleDetails}
            aria-expanded={showDetails}
            sx={{ textTransform: "none", px: 0.5, mb: 1 }}
          >
            {showDetails ? "Hide details" : "Show details"}
          </Button>
          <Collapse in={showDetails} unmountOnExit>
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#f6f7f9",
                border: "1px solid #ececf0",
                borderRadius: "8px",
                p: 1.5,
                m: 0,
                mb: 2,
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {detailsText(error)}
            </Box>
          </Collapse>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload page
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.assign("/dashboard")}
            >
              Back to dashboard
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children ?? null;
    return this.props.variant === "section"
      ? this.renderSection()
      : this.renderPage(error);
  }
}

/**
 * Page-level boundary for routed content: navigating to another location
 * clears the error without a full reload. Must render inside the Router.
 */
export const RouteErrorBoundary: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  return (
    <ErrorBoundary variant="page" resetKey={location.key}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
